<template>
  <v-flex>
    <v-btn @click="dialogSearchReceipt = true" small color="primary" class="mt-4" outlined>
      <v-icon>search</v-icon>Buscar recepción
    </v-btn>

    <v-dialog
      persistent
      width="1300"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="dialogSearchReceipt"
      transition="dialog-bottom-transition"
    >
      <v-card class="rounded-xl overflow-hidden search-receptions">
        <v-toolbar flat color="primary" dark>
          <v-icon left>mdi-package-variant-closed</v-icon>
          <v-toolbar-title class="font-weight-bold">
            Recepciones pendientes de facturar
            <span class="toolbar-subtitle">
              · {{ filters?.location?.desc_loc_long || filters?.business?.text || 'Todas las ubicaciones' }}
            </span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon :loading="loading" @click="loadPending()">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn @click="dialogSearchReceipt = false" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-row no-gutters class="search-body">
          <!-- Suplidores con recepciones pendientes -->
          <v-col cols="12" md="4" lg="3" class="supplier-panel">
            <div class="pa-3">
              <v-text-field
                v-model="supplier_query"
                placeholder="Buscar suplidor o RNC"
                prepend-inner-icon="mdi-account-search"
                outlined
                dense
                clearable
                hide-details
                class="rounded-lg"
              ></v-text-field>
            </div>
            <v-list dense nav class="supplier-list pt-0">
              <v-list-item
                :input-value="selected_entity_id === null"
                color="primary"
                @click="selected_entity_id = null"
              >
                <v-list-item-avatar size="32" color="grey lighten-3">
                  <v-icon small color="grey darken-2">mdi-account-multiple</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">Todos los suplidores</v-list-item-title>
                  <v-list-item-subtitle>{{ suppliers.length }} suplidores</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small color="primary" outlined>{{ receptions.length }}</v-chip>
                </v-list-item-action>
              </v-list-item>

              <v-list-item
                v-for="supplier in filteredSuppliers"
                :key="supplier.entity_id"
                :input-value="selected_entity_id === supplier.entity_id"
                color="primary"
                @click="selected_entity_id = supplier.entity_id"
              >
                <v-list-item-avatar size="32" color="blue lighten-2">
                  <span class="white--text caption font-weight-bold">{{ initials(supplier.entity_name) }}</span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium supplier-name">
                    {{ supplier.entity_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ supplier.rnc || 'Sin RNC' }}
                    <span v-for="total in supplier.totals" :key="total.code">
                      · {{ currencyFormat(total.amount) }} {{ total.code }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small color="primary" outlined>{{ supplier.count }}</v-chip>
                </v-list-item-action>
              </v-list-item>

              <div v-if="!loading && filteredSuppliers.length === 0" class="text-center caption grey--text py-6">
                {{ receptions.length ? 'Ningún suplidor coincide' : 'Sin recepciones pendientes' }}
              </div>
            </v-list>
          </v-col>

          <!-- Recepciones -->
          <v-col cols="12" md="8" lg="9" class="d-flex flex-column">
            <div class="pa-3 d-flex flex-wrap align-center">
              <v-text-field
                v-model="search_query"
                placeholder="Orden, recepción, factura suplidor, suplidor o RNC"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                class="rounded-lg reception-search"
                autofocus
              ></v-text-field>
              <v-chip
                v-if="selectedSupplier"
                close
                color="primary"
                outlined
                class="ml-sm-3 mt-2 mt-sm-0"
                @click:close="selected_entity_id = null"
              >
                <v-icon left small>mdi-truck-outline</v-icon>{{ selectedSupplier.entity_name }}
              </v-chip>
            </div>

            <v-alert
              v-if="truncated"
              dense
              text
              type="warning"
              class="mx-3 mb-2"
            >
              Se muestran las {{ receptions.length }} recepciones más recientes. Usa el buscador para encontrar las demás.
            </v-alert>

            <v-data-table
              :headers="headers"
              :loading="loading"
              loading-text="Cargando recepciones pendientes..."
              :items="visibleReceptions"
              item-key="entry_id"
              selectable-key="_selectable"
              :item-class="(row) => (row._selectable ? '' : 'row-locked')"
              show-select
              group-by="order_id"
              group-desc
              sort-by="created_date"
              sort-desc
              v-model="reception_selected"
              class="modern-table flex-grow-1"
              hide-default-footer
              :items-per-page="-1"
              :mobile-breakpoint="0"
              fixed-header
              height="52vh"
            >
              <!-- Sin "seleccionar todo": solo se facturan juntas recepciones de la misma orden -->
              <template v-slot:[`header.data-table-select`]></template>
              <template v-slot:[`group.header`]="{ group, items, isOpen, toggle }">
                <td :colspan="headers.length + 1" class="group-header">
                  <div class="d-flex align-center">
                    <v-btn icon x-small class="mr-1" @click="toggle">
                      <v-icon small>{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                    </v-btn>
                    <span class="font-weight-bold primary--text">
                      Orden #{{ group.toString().padStart(10, '0') }}
                    </span>
                    <span class="grey--text text--darken-1 ml-2 text-truncate">
                      · {{ items[0].entity_name }} · {{ items.length }} recepción{{ items.length > 1 ? 'es' : '' }}
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn
                      x-small
                      text
                      color="primary"
                      :disabled="!items[0]._selectable"
                      @click="selectOrder(items)"
                    >
                      <v-icon left x-small>mdi-checkbox-multiple-marked-outline</v-icon>
                      Seleccionar orden
                    </v-btn>
                  </div>
                </td>
              </template>
              <template v-slot:[`item.entry_id`]="{ item }">
                <v-chip label small outlined color="secondary">
                  {{ item.entry_id.toString().padStart(8, '0') }}
                </v-chip>
              </template>
              <template v-slot:[`item.created_date`]="{ item }">
                <div class="text-no-wrap">
                  <div class="caption">{{ item.formatted_date }}</div>
                  <div class="caption" :class="ageClass(item._age)">
                    {{ item._age === 0 ? 'Hoy' : `hace ${item._age} día${item._age === 1 ? '' : 's'}` }}
                  </div>
                </div>
              </template>
              <template v-slot:[`item.supplier_invoice_number`]="{ item }">
                <span v-if="item.supplier_invoice_number" class="font-weight-medium">
                  {{ item.supplier_invoice_number }}
                </span>
                <span v-else class="caption grey--text">—</span>
              </template>
              <template v-slot:[`item.entity_name`]="{ item }">
                <div class="d-flex flex-column">
                  <span class="font-weight-medium grey--text text--darken-3">{{ item.entity_name }}</span>
                  <span class="caption grey--text">{{ item.rnc }}</span>
                </div>
              </template>
              <template v-slot:[`item.location_description_long`]="{ item }">
                <span class="caption">{{ item.location_description_long }}</span>
              </template>
              <template v-slot:[`item._amount`]="{ item }">
                <div class="text-right text-no-wrap">
                  <span class="font-weight-bold">{{ currencyFormat(item._amount) }}</span>
                  <span class="caption grey--text ml-1">{{ item.currency_code }}</span>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-10">
                  <v-icon size="64" color="grey lighten-2">mdi-package-variant-closed-remove</v-icon>
                  <div class="text-body-1 grey--text mt-3">No hay recepciones pendientes de facturar</div>
                </div>
              </template>
              <template v-slot:no-results>
                <div class="text-center py-8">
                  <v-icon size="56" color="grey lighten-2">mdi-magnify-close</v-icon>
                  <div class="text-body-2 grey--text mt-2">Ninguna recepción pendiente coincide con la búsqueda</div>
                  <v-btn
                    v-if="search_query"
                    small
                    text
                    color="primary"
                    class="mt-2"
                    :loading="checking"
                    @click="checkIfAlreadyInvoiced()"
                  >
                    <v-icon left small>mdi-file-search-outline</v-icon>
                    Verificar si ya fue facturada
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 flex-wrap">
          <div v-if="reception_selected.length" class="caption grey--text text--darken-1">
            Orden #{{ reception_selected[0].order_id.toString().padStart(10, '0') }} ·
            {{ reception_selected[0].entity_name }} ·
            <strong>{{ currencyFormat(selectedTotal) }} {{ reception_selected[0].currency_code }}</strong>
            <span class="ml-1">(subtotal sin ITBIS)</span>
          </div>
          <div v-else class="caption grey--text">
            Selecciona una o varias recepciones de la misma orden
          </div>
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="dialogSearchReceipt = false" class="text-none">Cancelar</v-btn>
          <v-btn color="success" @click="selectItem" :disabled="reception_selected.length === 0"
            class="px-6 rounded-lg elevation-2">
            <v-icon left>mdi-file-document-edit-outline</v-icon>
            Facturar {{ reception_selected.length || '' }} recepción{{ reception_selected.length > 1 ? 'es' : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>
<script>
import moment from "moment";

// Normaliza para buscar sin distinguir mayúsculas ni acentos
const normalize = (value) =>
  (value == null ? "" : String(value))
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase();

const PENDING_LIMIT = 2000;

export default {
  data: () => ({
    loading: false,
    checking: false,
    search_query: null,
    supplier_query: null,
    selected_entity_id: null,
    reception_selected: [],
    receptions: [],
    dialogSearchReceipt: false,
    headers: [
      { text: "Recepción", value: "entry_id", align: "start", class: "font-weight-bold" },
      { text: "Fecha", value: "created_date", align: "start" },
      { text: "Factura suplidor", value: "supplier_invoice_number", align: "start" },
      { text: "Suplidor", value: "entity_name", align: "start" },
      { text: "Ubicación", value: "location_description_long", align: "start" },
      { text: "Subtotal", value: "_amount", align: "end" },
    ],
  }),

  props: {
    types_receptions: {
      type: Number,
    },
    filters: {
      type: Object
    }
  },
  watch: {
    dialogSearchReceipt(val) {
      if (val) this.loadPending();
    },
  },
  computed: {
    truncated() {
      return this.receptions.length >= PENDING_LIMIT;
    },
    // Filas para la tabla: se agregan campos calculados sin tocar los objetos originales
    rows() {
      const lockedOrder = this.reception_selected.length ? this.reception_selected[0].order_id : null;
      const today = moment().startOf("day");
      return this.receptions.map((reception) => ({
        ...reception,
        _amount: this.receptionAmount(reception),
        _age: Math.max(0, today.diff(moment(reception.created_date).startOf("day"), "days")),
        _selectable: lockedOrder === null || reception.order_id === lockedOrder,
        _search: normalize(
          [
            reception.order_id,
            reception.order_id.toString().padStart(10, "0"),
            reception.entry_id,
            reception.entry_id.toString().padStart(8, "0"),
            reception.supplier_invoice_number,
            reception.entity_name,
            reception.rnc,
            reception.location_description_long,
          ].join(" ")
        ),
      }));
    },
    visibleReceptions() {
      const terms = normalize(this.search_query).split(/\s+/).filter(Boolean);
      return this.rows.filter(
        (row) =>
          (this.selected_entity_id === null || row.entity_id === this.selected_entity_id) &&
          terms.every((term) => row._search.includes(term))
      );
    },
    suppliers() {
      const map = new Map();
      for (const row of this.rows) {
        if (!map.has(row.entity_id)) {
          map.set(row.entity_id, {
            entity_id: row.entity_id,
            entity_name: row.entity_name,
            rnc: row.rnc,
            count: 0,
            totalsMap: {},
          });
        }
        const supplier = map.get(row.entity_id);
        supplier.count++;
        const code = row.currency_code || "";
        supplier.totalsMap[code] = (supplier.totalsMap[code] || 0) + row._amount;
      }
      return [...map.values()]
        .map((supplier) => ({
          ...supplier,
          totals: Object.entries(supplier.totalsMap).map(([code, amount]) => ({ code, amount })),
        }))
        .sort((a, b) => b.count - a.count || a.entity_name.localeCompare(b.entity_name));
    },
    filteredSuppliers() {
      const terms = normalize(this.supplier_query).split(/\s+/).filter(Boolean);
      if (!terms.length) return this.suppliers;
      return this.suppliers.filter((supplier) => {
        const text = normalize(`${supplier.entity_name} ${supplier.rnc}`);
        return terms.every((term) => text.includes(term));
      });
    },
    selectedSupplier() {
      return this.suppliers.find((supplier) => supplier.entity_id === this.selected_entity_id) || null;
    },
    selectedTotal() {
      return this.reception_selected.reduce((sum, row) => sum + (row._amount || 0), 0);
    },
  },

  methods: {
    receptionAmount(reception) {
      const lines = Array.isArray(reception.order_item_entries) ? reception.order_item_entries : [];
      return lines.reduce((sum, line) => {
        const subtotal = parseFloat(line.quantity || 0) * parseFloat(line.price || 0);
        return sum + subtotal * (1 - parseFloat(line.discount || 0));
      }, 0);
    },
    initials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    ageClass(days) {
      if (days > 60) return "error--text";
      if (days > 30) return "orange--text text--darken-2";
      return "grey--text";
    },
    basePayload() {
      return {
        business_id: this.filters?.business?.id || 0,
        location_id: this.filters?.location?.id || 0,
      };
    },
    async loadPending() {
      try {
        this.loading = true;
        const responseRequest = await this.api_Post("shopping/search_entry_receptions", {
          ...this.basePayload(),
          invoiced: false,
          limit: PENDING_LIMIT,
        });
        const data = await responseRequest.json();
        this.receptions = Array.isArray(data) ? data : [];

        // Conserva la selección previa solo si esas recepciones siguen pendientes
        const ids = new Set(this.receptions.map((reception) => reception.entry_id));
        this.reception_selected = this.reception_selected.filter((row) => ids.has(row.entry_id));
        if (this.selected_entity_id !== null && !this.receptions.some((r) => r.entity_id === this.selected_entity_id)) {
          this.selected_entity_id = null;
        }
      } catch (error) {
        this.$toast.error(error.message);
        console.error(error.message);
      } finally {
        this.loading = false;
      }
    },
    selectOrder(items) {
      const ids = new Set(this.reception_selected.map((row) => row.entry_id));
      this.reception_selected = [
        ...this.reception_selected,
        ...items.filter((row) => row._selectable && !ids.has(row.entry_id)),
      ];
    },
    async checkIfAlreadyInvoiced() {
      const text = (this.search_query || "").trim();
      if (!text) return;
      try {
        this.checking = true;
        const res = await this.api_Post("shopping/search_entry_receptions", {
          ...this.basePayload(),
          search_query: text,
          invoiced: true,
        });
        const found = await res.json();
        if (Array.isArray(found) && found.length > 0) {
          const inv = found[0];
          const label = inv.invoice_number
            ? `Factura #${inv.invoice_number}`
            : "una factura existente";
          this.$toast.warning(
            `Esta recepción ya fue facturada (${label}). No puede facturarse de nuevo.`
          );
        } else {
          this.$toast.warning("No se encontraron recepciones con los filtros aplicados");
        }
      } catch (_) {
        this.$toast.warning("No se encontraron recepciones con los filtros aplicados");
      } finally {
        this.checking = false;
      }
    },
    selectItem() {
      // Se emiten los objetos originales del API, igual que antes
      const ids = new Set(this.reception_selected.map((row) => row.entry_id));
      const selected = this.receptions.filter((reception) => ids.has(reception.entry_id));
      this.$emit("selected-item", selected);
      this.reception_selected = [];
      this.dialogSearchReceipt = false;
    },
  },
};
</script>

<style scoped>
.toolbar-subtitle {
  font-weight: 400;
  opacity: 0.85;
  font-size: 0.9rem;
}

.search-body {
  min-height: 60vh;
}

.supplier-panel {
  background: #f7f9fc;
  border-right: 1px solid #e3e8ef;
}

.supplier-list {
  background: transparent !important;
  max-height: 60vh;
  overflow-y: auto;
}

.supplier-name {
  white-space: normal;
  line-height: 1.25;
}

.supplier-list ::v-deep .v-list-item__subtitle {
  white-space: normal;
}

.reception-search {
  max-width: 520px;
  width: 100%;
}

.group-header {
  background: #eef3fb !important;
}

.modern-table ::v-deep tr.row-locked td {
  opacity: 0.45;
}

.modern-table ::v-deep th {
  white-space: nowrap;
  font-weight: 700 !important;
}

@media (max-width: 960px) {
  .supplier-panel {
    border-right: none;
    border-bottom: 1px solid #e3e8ef;
  }

  .supplier-list {
    max-height: 30vh;
  }
}
</style>
